import { X } from "lucide-react-native";
import { FC, useCallback, useState } from "react";

import {
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { DemographicDistribution } from "@/types/resources";
import DemographicDistributionEditor from "./DemographicDistributionEditor";

interface BehaviorEditorProps {
  disabled?: boolean;
  distribution: DemographicDistribution<"behavior">[];
  onChange: (distribution: DemographicDistribution<"behavior">[]) => void;
}

const MAX_ITEMS = 8;

const equalWeights = (count: number): number[] => {
  const base = Math.floor(100 / count);
  const remainder = 100 - base * count;
  return Array.from(
    { length: count },
    (_, i) => (base + (i < remainder ? 1 : 0)) / 100
  );
};

const BehaviorEditor: FC<BehaviorEditorProps> = ({
  disabled = false,
  distribution,
  onChange,
}) => {
  const { i18n } = useLocalization();
  const [inputValue, setInputValue] = useState("");
  const [weightEditorOpen, setWeightEditorOpen] = useState(false);

  const commit = useCallback(() => {
    const val = inputValue.trim();
    if (!val || distribution.length >= MAX_ITEMS) return;
    if (distribution.some((d) => d.value === val)) {
      setInputValue("");
      return;
    }
    const next: DemographicDistribution<"behavior">[] = [
      ...distribution,
      { label: val, value: val, weight: 0 },
    ];
    const weights = equalWeights(next.length);
    onChange(next.map((d, i) => ({ ...d, weight: weights[i] })));
    setInputValue("");
  }, [distribution, inputValue, onChange]);

  const remove = useCallback(
    (value: string) => {
      const next = distribution.filter((d) => d.value !== value);
      if (next.length === 0) {
        onChange([]);
        return;
      }
      const weights = equalWeights(next.length);
      onChange(next.map((d, i) => ({ ...d, weight: weights[i] })));
    },
    [distribution, onChange]
  );

  const canEditWeights = distribution.length >= 1 && !disabled;
  const otherPct = Math.max(
    0,
    100 - distribution.reduce((sum, d) => sum + Math.round(d.weight * 100), 0)
  );

  return (
    <VStack className="gap-3">
      {distribution.length > 0 && (
        <HStack className="flex-wrap gap-2">
          {distribution.map((d) => {
            const pct = Math.round(d.weight * 100);
            return (
              <HStack
                className="items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 border border-outline-200"
                key={d.value}
              >
                <Text className="text-xs text-typography-600">{d.label}</Text>
                <Text className="text-xs text-outline-300 mx-0.5">|</Text>
                {canEditWeights ? (
                  <Pressable onPress={() => setWeightEditorOpen(true)}>
                    <Text className="text-xs font-semibold text-primary-600">
                      {i18n.numberToPercentage(pct, { precision: 0 })}
                    </Text>
                  </Pressable>
                ) : (
                  <Text className="text-xs font-semibold text-primary-600">
                    {i18n.numberToPercentage(pct, { precision: 0 })}
                  </Text>
                )}
                <Pressable disabled={disabled} onPress={() => remove(d.value)}>
                  <X color="rgb(var(--color-typography-400))" size={12} />
                </Pressable>
              </HStack>
            );
          })}
          {otherPct > 0 && (
            <HStack className="items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 border border-dashed border-outline-200">
              <Text className="text-xs text-typography-400">
                {i18n.t("misc.otherValues")}
              </Text>
              <Text className="text-xs text-outline-300 mx-0.5">|</Text>
              <Text className="text-xs font-semibold text-typography-400">
                {i18n.numberToPercentage(otherPct, { precision: 0 })}
              </Text>
            </HStack>
          )}
        </HStack>
      )}

      {distribution.length < MAX_ITEMS && (
        <Input
          className="border border-outline-200 rounded-xl bg-background-50"
          isDisabled={disabled}
          size="sm"
        >
          <InputField
            className="text-sm"
            onChangeText={(text) => {
              setInputValue(text);
            }}
            onSubmitEditing={commit}
            placeholder={i18n.t("dashboardNavigator.behaviorPlaceholder")}
            returnKeyType="done"
            value={inputValue}
          />
        </Input>
      )}

      <DemographicDistributionEditor
        distribution={distribution}
        onChange={onChange as (dist: DemographicDistribution[]) => void}
        onOpenChange={setWeightEditorOpen}
        open={weightEditorOpen}
        title={i18n.t("dashboardNavigator.behaviorLabel")}
      />
    </VStack>
  );
};

export default BehaviorEditor;
